import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/hunting-gallery-deer-blind.jpg";
import heroPicture from "@/assets/hunting-gallery-deer-blind.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import { HuntingTarpForm } from "@/components/HuntingTarpForm";

export const Route = createFileRoute("/deer-blind-covers")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Custom Deer Blind Covers & Roof Skins in Texas | Texas Made Shade",
      description:
        "Custom deer blind covers, replacement roof skins and full soft-skin enclosures for box, tower and tripod blinds — patterned to your frame and built in.",
      image: hero,
      imageAlt: "Custom tan canvas cover on a Texas box deer blind in brush country at sunset",
      socialTitle: "Custom Deer Blind Covers — Patterned & Sewn in Seguin, TX",
      socialDescription:
        "Replacement roof skins, soft-skin enclosures and patterned deer blind covers built to last through long Texas hunting seasons.",
      keywords:
        "deer blind covers Texas, deer blind roof skins, custom deer blind enclosures, box blind covers, tower blind covers, tripod blind covers, Sunbrella deer blind, Top Gun deer blind, Aqualon Edge, Seguin TX, San Antonio, Hill Country deer blinds, South Texas brush country",
    }),
    scripts: serviceHeadScripts({
      path: "/deer-blind-covers",
      serviceName: "Deer Blind Covers",
      breadcrumbName: "Deer Blind Covers",
      description: "Custom canvas and vinyl deer blind covers, replacement tops and skins built for Texas hunting season.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/deer-blind-covers"
      serviceName="Deer Blind Covers"
      breadcrumbs={[
        { label: "Custom Hunting & Fishing Covers", to: "/hunting-fishing-covers" },
        { label: "Custom Deer Blind Covers & Roof Skins" },
      ]}
      eyebrow="★ DEER BLIND COVERS ★"
      title="Custom Deer Blind Covers, Roof Skins & Soft-Skin Enclosures"
      intro="We pattern and sew custom deer blind covers for box blinds, tower blinds and tripod stands across Central Texas, South Texas and the Hill Country. Every cover is built to your blind's exact frame so it doesn't flap, tear or wear at the corners through long Texas hunting seasons."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Patterned to Your Blind · Seguin, TX",
        headline: "Need a Replacement Skin or Full Enclosure? Get a Free Quote.",
        subhead:
          "Send us your blind dimensions (or photos with a tape measure) and we'll come back with fabric options and a written estimate.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Deer Blind Cover")}&notes=${encodeURIComponent("Deer blind cover — please describe your blind type (box / tower / tripod), dimensions, and whether you need a roof skin or full enclosure.")}`,
        primaryLabel: "Get My Deer Blind Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Replacement roof skins patterned to your blind frame",
        "Full soft-skin enclosures with zip-down windows and shoot-through ports",
        "Reinforced corners, double-stitched seams and rot-proof bonded thread",
        "Snap and shock-cord skirts for a tight, quiet fit",
        "Sunbrella® Marine canvas, Top Gun® and Aqualon Edge fabric options",
        "On-site patterning across South Texas and the Hill Country (trip fee)",
      ]}
      body={[
        {
          heading: "Replacement Roof Skins for Box & Tower Blinds",
          text: "Patterned to your existing frame so the cover lays tight, sheds water and won't flap in a Texas norther. Built in heavy marine canvas with reinforced corners.",
          bullets: [
            "Patterned to box, tower or tripod blind frames",
            "Heavy bonded marine thread that resists UV rot",
            "Snap, shock-cord or grommet skirt options",
          ],
        },
        {
          heading: "Full Soft-Skin Enclosures with Windows & Shoot-Through Ports",
          text: "Convert an open frame into a quiet, weather-tight blind. Zip-down windows, shoot-through ports and reinforced corners make for a hunt-ready enclosure that lasts season after season.",
          bullets: [
            "YKK marine zippers on every window",
            "Reinforced shoot-through ports",
            "Double-stitched seams at every stress point",
          ],
          cta: { label: "See Blind Curtains & Drop Panels", href: "/curtains" },
        },
        {
          heading: "Fabric Options for Texas Hunting Seasons",
          text: "We carry Sunbrella® Marine, Top Gun® and Aqualon Edge — all UV-stable, water-repellent and mildew-resistant. Tell us where the blind sits and how long you need it to last and we'll match the right fabric to the job.",
          cta: { label: "Browse Hunting & Marine Fabrics", href: "/fabrics" },
        },
      ]}
      applicationsEyebrow="BLIND TYPES WE COVER"
      applicationsHeading="Blind Types We Cover"
      applications={[
        "Box Blinds",
        "Tower Blinds",
        "Tripod Stands",
        "Bow Blinds",
        "Hill Country Stands",
        "South Texas Brush Blinds",
      ]}
      faqs={[
        {
          q: "Do you make custom deer blind covers in Texas?",
          a: "Yes — full canvas deer blind covers, replacement roof skins, and complete soft-skin enclosures for box blinds, tower blinds, and tripod stands. We pattern to your specific blind so the cover doesn't flap or tear at the corners.",
        },
        {
          q: "What fabric holds up best for a Texas deer blind?",
          a: "We typically recommend Sunbrella® Marine, Top Gun® or Aqualon Edge — all UV-stable, water-repellent and mildew-resistant. The right pick depends on sun exposure, brush abrasion and how long you need the cover to last.",
        },
        {
          q: "Do you pattern on-site at the lease?",
          a: "Yes — on-site patterning across South Texas and the Hill Country is available for a trip fee. Most customers send photos and dimensions for an in-shop build, then we ship or fit at our Seguin shop.",
        },
        {
          q: "How long does a custom deer blind cover take?",
          a: "Most deer blind covers run 1–5 weeks depending on the queue. Rush jobs are available with a rush fee — contact us with your timeline.",
        },
      ]}
    >
      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Request a Quote</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Tell Us About Your Blind
          </h2>
          <p className="mt-3 text-muted-foreground">
            Share dimensions, photos and your fabric preference. We'll come back with a written estimate.{" "}
            <Link to="/hunting-fishing-covers" className="font-semibold text-navy-deep underline">
              Or browse our full hunting cover lineup →
            </Link>
          </p>
          <div className="mt-8">
            <HuntingTarpForm />
          </div>
        </div>
      </section>
    </ServicePage>
  ),
});
